/**
 * Jump, double jump, dodge/flip and flip cancel — the airborne state machine.
 *
 * This is a transcription of RocketSim's `Car::_UpdateJump`,
 * `Car::_UpdateDoubleJumpOrFlip` and the flip half of `Car::_UpdateAirTorque`,
 * in the order RocketSim runs them, with every magic number replaced by the
 * corresponding symbol from ./constants. RocketSim is the source we follow for
 * this system because it is the only one of the three that models flip cancel
 * at all (see the FLIP_CANCEL_ENABLED note in ./constants — RLUtilities has no
 * flip cancel, which is a model split, not a tuning difference).
 *
 * WHAT THIS FILE DOES NOT DO
 *   - It never applies gravity and never integrates position. The caller's
 *     integrator owns both.
 *   - It never integrates the orientation matrix. `stepJump` only ADDS to
 *     `car.angularVelocity`; the caller must run exactly one orientation
 *     integration per tick.
 *
 * HOW IT COMPOSES WITH ./aerial (the question the brief asks to settle)
 *
 * In Rocket League a dodge OVERRIDES air control: it does not add to it. This
 * is verifiable in RocketSim's `_UpdateAirTorque`, which computes a local
 * `doAirControl` flag that starts `false` whenever `isFlipping` is set, and
 * only becomes `true` again in two cases:
 *
 *   1. the flip has zero torque (a "stall": pitch and yaw+roll both inside
 *      DODGE_COMPONENT_DEADZONE, so the normalized dodge direction is the zero
 *      vector) — air control for the whole flip;
 *   2. FLIP CANCEL — the player holds pitch with the same sign as the flip's
 *      pitch torque, which scales that torque by (1 - |pitch|) AND re-enables
 *      air control in the same branch.
 *
 * Even when air control is re-enabled, PITCH is locked out: RocketSim zeroes
 * the pitch torque while `isFlipping`, and for FLIP_PITCHLOCK_EXTRA_TIME after
 * the flip ends. Because its damping term is scaled by (1 - |pitch * scale|),
 * zeroing the pitch input reproduces both effects at once: no pitch torque and
 * FULL pitch damping. That is exactly what `stepAerialRotation` does when
 * handed `pitch: 0`, so the lockout is expressed here by rewriting the input
 * rather than by touching ./aerial.
 *
 * `stepJump` therefore returns the air-control decision instead of making it,
 * and the caller's per-tick car step is:
 *
 *     const jumpStep = stepJump(car, input, dt);
 *     if (jumpStep.airControl) stepAerialRotation(car, jumpStep.airControlInput, dt);
 *     else                     integrateOrientation(car, dt);
 *
 * TICK-BOUNDARY COMPARISONS
 *
 * Every timer here is a float accumulated one `dt` at a time, so it lands a few
 * ULPs either side of the constant it is compared against: 3 * (1/120) sums to
 * 0.024999999999999998, which is strictly less than JUMP_MIN_HOLD_TIME and
 * would silently buy the jump a FOURTH 0.62-scaled tick. `before`/`reached`
 * below absorb that drift. TIMER_EPSILON is a floating-point guard, not a
 * physics constant — it is orders of magnitude smaller than a tick.
 *
 * THE JUMP-BUTTON EDGE
 *
 * RocketSim edge-detects the jump button (`controls.jump && !lastControls.jump`).
 * Without the edge, holding jump would re-jump the instant the car lands and
 * would burn the double jump the instant the first jump's hold window expires.
 * The previous button level is `car.prevJumpDown`. It was a module-level WeakMap
 * keyed by the CarState — which meant a cloned or serialized car lost the bit
 * and would jump once more than the original — until CarState grew the field.
 */

import type { CarInput, CarState } from './car-state';
import {
  CAR_MAX_ANGULAR_SPEED,
  CAR_MAX_SPEED,
  DODGE_BACKWARD_EXTRA_X,
  DODGE_COMPONENT_DEADZONE,
  DODGE_COOLDOWN,
  DODGE_IMPULSE,
  DODGE_SPEED_SCALE_BACKWARD,
  DODGE_SPEED_SCALE_FORWARD,
  DODGE_SPEED_SCALE_SIDE,
  DODGE_TORQUE_FORWARD,
  DODGE_TORQUE_SIDE,
  DODGE_TORQUE_TIME,
  DOUBLE_JUMP_IMPULSE,
  DOUBLE_JUMP_MAX_DELAY,
  FLIP_CANCEL_ENABLED,
  FLIP_PITCHLOCK_TOTAL_TIME,
  FLIP_Z_DAMP,
  FLIP_Z_DAMP_END,
  FLIP_Z_DAMP_START,
  JUMP_HOLD_ACCELERATION,
  JUMP_IMPULSE,
  JUMP_MAX_HOLD_TIME,
  JUMP_MIN_HOLD_TIME,
  JUMP_PRE_MIN_ACCEL_SCALE,
  JUMP_RESET_TIME_PAD,
  TICK_RATE,
} from './constants';
import {
  addScaledTo,
  clampLengthBy,
  clone,
  col,
  dot,
  mulVec,
  normalize,
  set,
  sign,
  type Vec3,
  up,
  vec3,
} from './math';

/**
 * Forward-speed magnitude below which the "am I dodging against my direction of
 * travel?" test falls back to the sign of the dodge direction itself.
 *
 * [1-src: RocketSim `Car::_UpdateDoubleJumpOrFlip`, the literal
 *  `if (abs(forwardSpeed_UU) < 100.0f) shouldDodgeBackwards = dodgeDir.x < 0;`]
 * It is NOT in src/physics/constants.ts, which this file may not edit; it
 * belongs there next to DODGE_SPEED_SCALE_BACKWARD. Reported. Without it, a
 * front flip while rolling gently backwards would pick up the backward impulse
 * scaling (16/15 and up), which is wrong and visible on kickoff jitter.
 */
const DODGE_BACKWARD_SPEED_THRESHOLD = 100;

/** Slack for comparing a dt-accumulated timer against a constant. See header. */
const TIMER_EPSILON = 1e-9;

/** `t < limit`, immune to timer accumulation drift. */
const before = (t: number, limit: number): boolean => t < limit - TIMER_EPSILON;

/** `t >= limit`, immune to timer accumulation drift. */
const reached = (t: number, limit: number): boolean => t >= limit - TIMER_EPSILON;

/** Read the remembered jump-button state. Thin reader over `car.prevJumpDown`,
 *  kept so the callers that predate the field do not have to change. */
export const wasJumpDown = (car: CarState): boolean => car.prevJumpDown;

/** Seed the remembered jump-button state, e.g. after deserializing a car. */
export const setWasJumpDown = (car: CarState, down: boolean): void => {
  car.prevJumpDown = down;
};

/** What the caller must do with rotation this tick. */
export interface JumpStepResult {
  /**
   * True when `stepAerialRotation` should run. False while the dodge torque
   * owns the rotation (and while the car is on the ground) — in that case the
   * caller must still advance the orientation once, via `integrateOrientation`.
   */
  airControl: boolean;
  /**
   * The input to hand to `stepAerialRotation`. Identical to `input` except that
   * pitch is zeroed while the flip pitch-lockout is in effect. Meaningless when
   * `airControl` is false.
   */
  airControlInput: CarInput;
}

/**
 * Angular acceleration of an active dodge, in the car's LOCAL frame, ordered
 * (roll, pitch, yaw) = (forward, left, up) to match `aerialTorque` in ./aerial.
 *
 *   torque_local = (-dir.y * DODGE_TORQUE_SIDE, dir.x * DODGE_TORQUE_FORWARD, 0)
 *
 * exactly as documented on DODGE_TORQUE_SIDE. That expression already IS the 2D
 * cross product; per docs/PHYSICS_REFERENCE.md §6.3 its signs are transcribed,
 * never re-derived.
 *
 * RocketSim divides the direction by the tick-rate ratio before scaling, which
 * makes the delta-v per TICK independent of dt; reproduced here so the flip
 * spins up in the same number of ticks at any dt.
 *
 * `pitchInput` applies the flip cancel: when it pushes the same way as the
 * flip's own pitch torque, that component is scaled by (1 - |pitch|), reaching
 * exactly zero at full deflection. The ROLL component is never cancellable.
 *
 * Exposed separately from the integrator so the (1 - |pitch|) law can be
 * asserted numerically rather than inferred from a trajectory.
 */
export const dodgeTorqueLocal = (dodgeDirection: Vec3, pitchInput: number, dt: number): Vec3 => {
  const tickTimeScale = dt * TICK_RATE;
  const relRoll = -dodgeDirection.y / tickTimeScale;
  const relPitch = dodgeDirection.x / tickTimeScale;

  let pitchScale = 1;
  if (FLIP_CANCEL_ENABLED && relPitch !== 0 && pitchInput !== 0 && sign(relPitch) === sign(pitchInput)) {
    pitchScale = 1 - Math.min(Math.abs(pitchInput), 1);
  }

  return vec3(relRoll * DODGE_TORQUE_SIDE, relPitch * pitchScale * DODGE_TORQUE_FORWARD, 0);
};

/** True when a dodge started with this direction re-enables air control: the
 *  stall case (zero torque) or an active flip cancel. */
const dodgeAllowsAirControl = (dodgeDirection: Vec3, pitchInput: number): boolean => {
  if (dodgeDirection.x === 0 && dodgeDirection.y === 0) return true; // stall
  return (
    FLIP_CANCEL_ENABLED &&
    dodgeDirection.x !== 0 &&
    pitchInput !== 0 &&
    sign(dodgeDirection.x) === sign(pitchInput)
  );
};

/**
 * Begin a dodge. Sets the dodge state, applies the horizontal impulse and
 * consumes the flip. The torque itself is NOT applied on this tick: RocketSim
 * runs its air-torque pass before the flip is created, so the first tick of a
 * dodge is still a normal air-control tick.
 */
const startDodge = (car: CarState, input: CarInput, forwardSpeed: number): void => {
  car.hasFlip = false;
  car.isDodging = true;
  car.dodgeTime = 0;

  // Dodge direction in the car's local horizontal plane. x is forward, and it
  // is MINUS pitch: stick forward is negative pitch and must give a front flip.
  // y is yaw + roll, i.e. the same physical stick axis whichever modifier the
  // player is holding, and points along the car's `left` column (+Y at
  // identity), so a left dodge adds velocity toward +Y — the sign check in
  // docs/PHYSICS_REFERENCE.md §6.4.
  const rawX = -input.pitch;
  const rawY = input.yaw + input.roll;

  // Stall: both stick axes inside the component deadzone. The direction — and
  // therefore the whole dodge torque and impulse — collapses to zero, but the
  // flip is still consumed.
  const stalled =
    Math.abs(rawY) < DODGE_COMPONENT_DEADZONE && Math.abs(input.pitch) < DODGE_COMPONENT_DEADZONE;
  const dir = stalled ? vec3(0, 0, 0) : normalize(vec3(rawX, rawY, 0));

  // The TORQUE uses the direction as-is; the IMPULSE uses it after the
  // component deadzone. Both simulators apply the deadzone in that order, and
  // the difference is what keeps a near-pure flip's impulse pure while its
  // rotation still leans.
  set(car.dodgeDirection, dir.x, dir.y, 0);

  const impulseX = Math.abs(dir.x) < DODGE_COMPONENT_DEADZONE ? 0 : dir.x;
  const impulseY = Math.abs(dir.y) < DODGE_COMPONENT_DEADZONE ? 0 : dir.y;
  if (impulseX === 0 && impulseY === 0) return;

  const speedRatio = Math.abs(forwardSpeed) / CAR_MAX_SPEED;

  // "Backward" means dodging AGAINST the direction of travel, not dodging
  // toward the car's rear — that is why a front flip while reversing gets the
  // backward scaling. Below DODGE_BACKWARD_SPEED_THRESHOLD there is no reliable
  // direction of travel, so the dodge direction decides.
  const dodgingBackwards =
    Math.abs(forwardSpeed) < DODGE_BACKWARD_SPEED_THRESHOLD
      ? impulseX < 0
      : impulseX >= 0 !== forwardSpeed >= 0;

  const maxSpeedScaleX = dodgingBackwards ? DODGE_SPEED_SCALE_BACKWARD : DODGE_SPEED_SCALE_FORWARD;
  let dvX = impulseX * DODGE_IMPULSE * ((maxSpeedScaleX - 1) * speedRatio + 1);
  const dvY = impulseY * DODGE_IMPULSE * ((DODGE_SPEED_SCALE_SIDE - 1) * speedRatio + 1);
  if (dodgingBackwards) dvX *= DODGE_BACKWARD_EXTRA_X;

  // Applied in the YAW-FLATTENED frame, so the impulse is purely horizontal
  // however the car is tumbling. `flatLeft` is the same expression RocketSim
  // uses for what it calls `rightDir2D`: identical numbers, opposite label —
  // see docs/PHYSICS_REFERENCE.md §6.2.
  const fwd = col(car.orientation, 0);
  const flatForward = normalize(vec3(fwd.x, fwd.y, 0));
  const flatLeft = vec3(-flatForward.y, flatForward.x, 0);

  addScaledTo(car.velocity, flatForward, dvX);
  addScaledTo(car.velocity, flatLeft, dvY);
  clampLengthBy(car.velocity, CAR_MAX_SPEED);
};

/**
 * Advance the jump/dodge state machine by one tick.
 *
 * Mutates `car.velocity`, `car.angularVelocity` and the jump/dodge fields of
 * `car`. Reads `car.onGround` as the ground contact test; nothing here decides
 * whether the car is grounded.
 */
export const stepJump = (car: CarState, input: CarInput, dt: number): JumpStepResult => {
  const jumpPressed = input.jump && !car.prevJumpDown;
  car.prevJumpDown = input.jump;

  const tickTimeScale = dt * TICK_RATE;

  // The hold phase of the first jump is not a CarState field. It is recoverable
  // exactly, because `airTime` counts time since the jump ENDED and so is zero
  // for precisely as long as the jump is still running:
  //   airTime accumulates  <=>  hasJumped && !holding
  // Everything below keeps that invariant, in particular by never zeroing
  // airTime while hasJumped is still set.
  let holding = car.hasJumped && car.airTime === 0;

  // -------------------------------------------------------------------------
  // 1. Air torque — RocketSim's `_UpdateAirTorque`, which runs BEFORE the jump
  //    and flip updates. A flip created later this tick therefore gets a normal
  //    air-control tick before its torque starts.
  // -------------------------------------------------------------------------

  let airControl: boolean;

  if (car.onGround) {
    // RocketSim drops the flip the moment 3 wheels are back in contact, before
    // the air torque pass runs at all.
    car.isDodging = false;
    airControl = false;
  } else {
    if (car.isDodging) car.isDodging = before(car.dodgeTime, DODGE_TORQUE_TIME);

    if (car.isDodging) {
      airControl = dodgeAllowsAirControl(car.dodgeDirection, input.pitch);
      const local = dodgeTorqueLocal(car.dodgeDirection, input.pitch, dt);
      if (local.x !== 0 || local.y !== 0) {
        addScaledTo(car.angularVelocity, mulVec(car.orientation, local), dt);
        clampLengthBy(car.angularVelocity, CAR_MAX_ANGULAR_SPEED);
      }
    } else {
      airControl = true;
    }
  }

  // Pitch is locked out for the whole flip plus FLIP_PITCHLOCK_EXTRA_TIME. A
  // double jump does not lock pitch, and it leaves dodgeTime at zero, which is
  // what distinguishes the two here.
  const pitchLocked =
    car.isDodging || (car.dodgeTime > 0 && before(car.dodgeTime, FLIP_PITCHLOCK_TOTAL_TIME));
  const airControlInput = pitchLocked ? { ...input, pitch: 0 } : input;

  // -------------------------------------------------------------------------
  // 2. Jump — RocketSim's `_UpdateJump`.
  // -------------------------------------------------------------------------

  if (car.onGround && !holding) {
    // JUMP_RESET_TIME_PAD keeps the jump "used" for a few ticks after takeoff,
    // so that still touching the ground on the tick the hold force ends does
    // not hand the player a free second jump.
    const withinResetPad =
      car.hasJumped && before(car.jumpHoldTime, JUMP_MIN_HOLD_TIME + JUMP_RESET_TIME_PAD);
    if (!withinResetPad) {
      car.hasJumped = false;
      car.jumpHoldTime = 0;
      car.airTime = 0;
    }
  }

  if (holding) {
    // The hold force survives a release for JUMP_MIN_HOLD_TIME — this is what
    // puts a floor under jump height — and dies at JUMP_MAX_HOLD_TIME whether
    // the button is held or not.
    holding =
      before(car.jumpHoldTime, JUMP_MIN_HOLD_TIME) ||
      (input.jump && before(car.jumpHoldTime, JUMP_MAX_HOLD_TIME));
  } else if (car.onGround && jumpPressed) {
    holding = true;
    car.jumpHoldTime = 0;
    car.airTime = 0;
    addScaledTo(car.velocity, up(car.orientation), JUMP_IMPULSE);
    clampLengthBy(car.velocity, CAR_MAX_SPEED);
  }

  if (holding) {
    car.hasJumped = true;
    const scale = before(car.jumpHoldTime, JUMP_MIN_HOLD_TIME) ? JUMP_PRE_MIN_ACCEL_SCALE : 1;
    addScaledTo(car.velocity, up(car.orientation), JUMP_HOLD_ACCELERATION * scale * dt);
    clampLengthBy(car.velocity, CAR_MAX_SPEED);
  }

  if (car.hasJumped) car.jumpHoldTime += dt;

  // -------------------------------------------------------------------------
  // 3. Double jump / flip — RocketSim's `_UpdateDoubleJumpOrFlip`.
  // -------------------------------------------------------------------------

  if (car.onGround) {
    // Landing refreshes the flip and clears the dodge. There is no landing
    // recovery: DODGE_COOLDOWN is a confirmed ABSENCE in both simulators, so
    // the car may dodge again on the very next airborne tick.
    car.hasFlip = true;
    car.isDodging = false;
    car.dodgeTime = 0;
    set(car.dodgeDirection, 0, 0, 0);
    car.dodgeCooldown = DODGE_COOLDOWN;
  } else {
    // Measured from the END of the jump, which is why the wall-clock window is
    // up to DOUBLE_JUMP_MAX_DELAY + JUMP_MAX_HOLD_TIME. A car that drove off a
    // ledge without jumping has hasJumped === false and therefore an airTime
    // that never leaves zero: its flip stays available indefinitely, which is
    // the real game's behaviour too.
    if (car.hasJumped && !holding) car.airTime += dt;
    else car.airTime = 0;

    if (jumpPressed && car.hasFlip && car.dodgeCooldown <= 0 && before(car.airTime, DOUBLE_JUMP_MAX_DELAY)) {
      // The deadzone is the SUM of the three absolute stick axes, not a vector
      // magnitude: a half-diagonal input dodges where a vector norm would not.
      // It is a property of the BODY in RocketSim's struct (./car-config); every
      // shipped body has 0.5, so this reads the same number DODGE_DEADZONE does.
      const inputMagnitude = Math.abs(input.pitch) + Math.abs(input.yaw) + Math.abs(input.roll);
      if (inputMagnitude >= car.config.dodgeDeadzone) {
        startDodge(car, input, dot(car.velocity, col(car.orientation, 0)));
      } else {
        car.hasFlip = false;
        addScaledTo(car.velocity, up(car.orientation), DOUBLE_JUMP_IMPULSE);
        clampLengthBy(car.velocity, CAR_MAX_SPEED);
      }
    }
  }

  car.dodgeCooldown = Math.max(0, car.dodgeCooldown - dt);

  if (car.isDodging) {
    car.dodgeTime += dt;
    // Flip z-damping: what makes a flip kill upward momentum. Only while the
    // torque is still running, only after FLIP_Z_DAMP_START, and only on the
    // way down (or inside the FLIP_Z_DAMP_END window).
    if (
      car.dodgeTime <= DODGE_TORQUE_TIME + TIMER_EPSILON &&
      reached(car.dodgeTime, FLIP_Z_DAMP_START) &&
      (car.velocity.z < 0 || before(car.dodgeTime, FLIP_Z_DAMP_END))
    ) {
      car.velocity.z *= Math.pow(1 - FLIP_Z_DAMP, tickTimeScale);
    }
  } else if (car.dodgeTime > 0) {
    // Keep counting after the torque ends so FLIP_PITCHLOCK_EXTRA_TIME works.
    car.dodgeTime += dt;
  }

  return { airControl, airControlInput };
};

/** Convenience snapshot of the derived flags this module owns, for callers and
 *  tests that would otherwise have to re-derive them. */
export const jumpPhase = (car: CarState): {
  holdingJump: boolean;
  pitchLocked: boolean;
  dodgeDirection: Vec3;
} => ({
  holdingJump: car.hasJumped && car.airTime === 0,
  pitchLocked:
    car.isDodging || (car.dodgeTime > 0 && before(car.dodgeTime, FLIP_PITCHLOCK_TOTAL_TIME)),
  dodgeDirection: clone(car.dodgeDirection),
});
